"use client";

/**
 * The composer's two pickers: `@` for agents and the lanes of this task, `/`
 * for skills, plus the highlight overlay's content. Moved out of MessageInput
 * verbatim so the Dock's pill, which is the composer on the task screen with
 * one-composer-everywhere on, runs the SAME pickers rather than a copy. The
 * host owns the words, the textarea and the two dropdown components; this
 * hook owns which list is open, what it lists and what a pick does.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useLaneStates } from "@/stores/selectors";
import { useTeamViewStore } from "@/stores/teamViewStore";
import {
  generateHighlightedContent,
  type AgentInfo,
  type SkillInfo,
} from "./HighlightOverlay";

export interface MentionSkillPickersArgs {
  message: string;
  setMessage: (next: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  agents: AgentInfo[];
  skills: SkillInfo[];
  /** The CREATED task in view (never a pending placeholder), or null. */
  createdTaskId: string | null;
  /** The lane whose screen has the column, or null for Bash. */
  laneInView: string | null;
}

export interface MentionSkillPickers {
  /** Opens or shuts the two lists. Scan the words `message` holds (or is about
   *  to hold in this same handler; the order of the two calls does not matter):
   *  a pick splices `message` at the positions found here, so a host whose
   *  `message` is not the string it scanned gets a wrong splice. Its identity
   *  never changes, so a host may scan from an effect keyed on it. */
  scan: (value: string, cursorPos: number) => void;
  mention: {
    open: boolean;
    agents: AgentInfo[];
    triggerPos: number;
    onSelect: (agent: AgentInfo) => void;
    onClose: () => void;
    /** Every slug the list could offer (lanes first, then agents), unfiltered: the highlight's vocabulary. */
    roster: string[];
  };
  skill: {
    open: boolean;
    skills: SkillInfo[];
    triggerPos: number;
    onSelect: (skill: SkillInfo) => void;
    onClose: () => void;
    /** Every skill slug, unfiltered. */
    roster: string[];
  };
  /** Sanitized HTML for the highlight overlay. */
  highlightedContent: string;
}

/** The start of the trigger the caret sits in, or -1: walk back from the caret
 *  to the trigger character, giving up at the first space or newline. */
function triggerStart(
  value: string,
  cursorPos: number,
  trigger: string
): number {
  // A caret PAST the words stands on a line the serializer trimmed, where no
  // token can be (the rich editor answers `text.length + 1` on a trailing
  // empty line). `value[value.length]` is undefined, neither a space nor a
  // newline, so without this the walk runs on into the line above and a
  // trigger THERE opens a list under an empty line. A textarea's
  // selectionStart never exceeds its value, so this is neutral for the box.
  if (cursorPos > value.length) return -1;
  for (let i = cursorPos - 1; i >= 0; i--) {
    const char = value[i];
    if (char === trigger) return i;
    if (char === " " || char === "\n") break;
  }
  return -1;
}

/** A trigger only counts at the start of a word. */
function atWordStart(value: string, start: number): boolean {
  const charBefore = start > 0 ? value[start - 1] : " ";
  return charBefore === " " || charBefore === "\n" || start === 0;
}

export function useMentionSkillPickers({
  message,
  setMessage,
  textareaRef,
  agents,
  skills,
  createdTaskId,
  laneInView,
}: MentionSkillPickersArgs): MentionSkillPickers {
  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionStartPos, setMentionStartPos] = useState(0);

  // /skill state
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [skillFilter, setSkillFilter] = useState("");
  const [skillStartPos, setSkillStartPos] = useState(0);

  const select = useTeamViewStore((s) => s.select);

  // §3.7: the lanes of this task a message can actually reach, ahead of the
  // agents. LIVE ones first: a person types `@` to reach someone still out, and
  // a lane that has reported back can only read it later. Roster order holds
  // inside each group. The badge is the lane's own word (§3.3), so this row and
  // the chip on the bench can never say different things about one helper.
  const lanes = useLaneStates(createdTaskId);
  const mentionables = useMemo<AgentInfo[]>(() => {
    const reachable = lanes.filter((l) => l.addressable);
    if (reachable.length === 0) return agents;
    const ordered = [
      ...reachable.filter((l) => l.live),
      ...reachable.filter((l) => !l.live),
    ];
    const laneEntries: AgentInfo[] = ordered.map((l) => ({
      id: l.teammateTaskId,
      name: l.handle,
      slug: l.handle,
      description: l.word.word,
      category: l.kind,
    }));
    return [...laneEntries, ...agents];
  }, [agents, lanes]);

  // A leading `@handle ` the person typed by hand IS a pick: the same selection
  // the chip and the dropdown row land on (§3.7). It stays in the text until
  // send normalizes it. Dropping a selection whose lane has left the roster is
  // the BENCH's job (§3.2): one owner, so the two cannot fight over it.
  //
  // It asks the DROPDOWN's question, `addressable`, and for the same reason:
  // selecting a lane with no inbox disables the very field mid-sentence.
  //
  // ONCE PER PREFIX, not once per render. This effect also runs on every change
  // of `laneInView`, and re-applying the prefix there made the typed lane a
  // trap: Bash's chip, an arrow key and a dropdown row each moved the key and
  // were overwritten within the same commit. The ref remembers the prefix
  // already acted on; a roster that has not landed yet leaves it unset, so the
  // pick still happens when the lane appears.
  const pickedHandle = useRef<string | null>(null);
  useEffect(() => {
    if (!createdTaskId) return;
    const m = /^@([^\s@]+)\s/.exec(message);
    const handle = m ? m[1].toLowerCase() : null;
    if (handle === null) {
      pickedHandle.current = null;
      return;
    }
    if (handle === pickedHandle.current) return;
    const typed = lanes.find(
      (l) => l.addressable && l.handle.toLowerCase() === handle
    );
    if (!typed) return;
    pickedHandle.current = handle;
    if (typed.teammateTaskId !== laneInView)
      select(createdTaskId, typed.teammateTaskId);
  }, [message, lanes, laneInView, createdTaskId, select]);

  const filteredAgents = useMemo(() => {
    return mentionables.filter(
      (agent) =>
        agent.slug.toLowerCase().includes(mentionFilter.toLowerCase()) ||
        agent.name.toLowerCase().includes(mentionFilter.toLowerCase())
    );
  }, [mentionables, mentionFilter]);

  const filteredSkills = useMemo(() => {
    return skills.filter(
      (skill) =>
        skill.slug.toLowerCase().includes(skillFilter.toLowerCase()) ||
        skill.name.toLowerCase().includes(skillFilter.toLowerCase())
    );
  }, [skills, skillFilter]);

  // `scan` never changes identity: the pill's leaf scans from an EFFECT keyed
  // on it, and were it to change with the roster's size, a list the person shut
  // with Escape would reopen the moment a helper landed or left, and the next
  // Enter would pick a row instead of sending. So the two counts are read
  // through a ref, written in an effect (react-hooks/refs bars a render write);
  // this effect runs before any host effect that scans, in the same commit.
  const counts = useRef({
    mention: mentionables.length,
    skill: skills.length,
  });
  useEffect(() => {
    counts.current = { mention: mentionables.length, skill: skills.length };
  }, [mentionables.length, skills.length]);

  const scan = useCallback((value: string, cursorPos: number) => {
    const mentionStart = triggerStart(value, cursorPos, "@");
    const skillStart = triggerStart(value, cursorPos, "/");

    if (
      mentionStart !== -1 &&
      counts.current.mention > 0 &&
      atWordStart(value, mentionStart)
    ) {
      setShowMentionDropdown(true);
      setShowSkillDropdown(false);
      setMentionFilter(value.slice(mentionStart + 1, cursorPos));
      setMentionStartPos(mentionStart);
      return;
    }

    if (
      skillStart !== -1 &&
      counts.current.skill > 0 &&
      atWordStart(value, skillStart)
    ) {
      setShowSkillDropdown(true);
      setShowMentionDropdown(false);
      setSkillFilter(value.slice(skillStart + 1, cursorPos));
      setSkillStartPos(skillStart);
      return;
    }

    setShowMentionDropdown(false);
    setShowSkillDropdown(false);
  }, []);

  const insertMention = useCallback(
    (agent: AgentInfo) => {
      const beforeMention = message.slice(0, mentionStartPos);
      const afterCursor = message.slice(
        mentionStartPos + mentionFilter.length + 1
      );
      // R39: the ROSTER decides, never the row's badge: R38 renamed a helper's,
      // and a MID-SENTENCE pick has no `^@handle ` effect to fall back on.
      const picked = lanes.find((l) => l.teammateTaskId === agent.id);
      // §3.7: picking a lane IS selecting it, and nothing is written into the
      // words: the chip carries the recipient now, so a mention left in the
      // text would be the same thing said twice. The filter the person typed
      // goes with the pick.
      const newMessage = picked
        ? `${beforeMention}${afterCursor}`
        : `${beforeMention}@${agent.slug} ${afterCursor}`;
      if (picked && createdTaskId) select(createdTaskId, picked.teammateTaskId);
      setMessage(newMessage);
      setShowMentionDropdown(false);
      setMentionFilter("");
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = picked
            ? mentionStartPos
            : mentionStartPos + agent.slug.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    },
    [
      message,
      mentionStartPos,
      mentionFilter,
      lanes,
      createdTaskId,
      select,
      setMessage,
      textareaRef,
    ]
  );

  const insertSkill = useCallback(
    (skill: SkillInfo) => {
      const beforeSkill = message.slice(0, skillStartPos);
      const afterCursor = message.slice(skillStartPos + skillFilter.length + 1);
      setMessage(`${beforeSkill}/${skill.slug} ${afterCursor}`);
      setShowSkillDropdown(false);
      setSkillFilter("");
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = skillStartPos + skill.slug.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    },
    [message, skillStartPos, skillFilter, setMessage, textareaRef]
  );

  const closeMention = useCallback(() => setShowMentionDropdown(false), []);
  const closeSkill = useCallback(() => setShowSkillDropdown(false), []);

  const mentionRoster = useMemo(
    () => mentionables.map((a) => a.slug),
    [mentionables]
  );
  const skillRoster = useMemo(() => skills.map((s) => s.slug), [skills]);

  // Content is sanitized in generateHighlightedContent to prevent XSS
  const highlightedContent = useMemo(
    () => generateHighlightedContent(message, mentionables, skills),
    [message, mentionables, skills]
  );

  return {
    scan,
    mention: {
      open: showMentionDropdown,
      agents: filteredAgents,
      triggerPos: mentionStartPos,
      onSelect: insertMention,
      onClose: closeMention,
      roster: mentionRoster,
    },
    skill: {
      open: showSkillDropdown,
      skills: filteredSkills,
      triggerPos: skillStartPos,
      onSelect: insertSkill,
      onClose: closeSkill,
      roster: skillRoster,
    },
    highlightedContent,
  };
}
